import { forwardRef, useState } from "react";
import {
  AnimatePresence,
  Reorder,
  motion,
  useDragControls,
  useReducedMotion,
} from "framer-motion";
import { Check, GripVertical, Loader2, MessageSquareText, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { PipelineStage } from "@/api/onboarding-api";
import { ExampleChips } from "../hints";
import { StepCard, StepCardLabel } from "../step-card";
import { SPRING } from "../motion";

/** Instructions worth trying — each fills the box rather than running. */
const PIPELINE_EXAMPLES = ["Add Screening after New", "Rename Reviewing to Diligence", "Remove the last stage"];

const PALETTE = [
  "#3B82F6",
  "#F59E0B",
  "#EC4899",
  "#8B5CF6",
  "#10B981",
  "#6366F1",
  "#14B8A6",
  "#F43F5E",
];

/**
 * Stages carry a client-only id while they're being edited.
 *
 * Titles can't be the React key — they change on every keystroke of a rename,
 * which would remount the row and drop the caret. The id also survives a
 * round-trip through the natural-language parser, so an untouched stage keeps
 * its identity (and therefore its layout position) instead of animating out
 * and back in.
 */
export interface EditableStage extends PipelineStage {
  id: string;
}

let idCounter = 0;
const nextId = () => `stage-${++idCounter}`;

export function withIds(stages: PipelineStage[]): EditableStage[] {
  return stages.map((s) => ({ ...s, id: nextId() }));
}

/** Drops the client-only id so the result matches what the API expects. */
export function stripIds(stages: EditableStage[]): PipelineStage[] {
  return stages.map((s) => ({
    title: s.title,
    colour: s.colour,
    description: s.description,
    order: s.order,
  }));
}

/** Renumbers after any structural change so `order` always matches position. */
export function renumber(stages: EditableStage[]): EditableStage[] {
  return stages.map((s, i) => ({
    ...s,
    colour: s.colour || PALETTE[i % PALETTE.length],
    order: i + 1,
  }));
}

/**
 * Reconciles a parser result against what's on screen, matching by title so
 * stages the instruction didn't touch keep their id.
 */
export function mergeParsed(
  previous: EditableStage[],
  parsed: PipelineStage[]
): EditableStage[] {
  const byTitle = new Map(previous.map((s) => [s.title.toLowerCase(), s]));
  return renumber(
    parsed.map((s) => ({
      ...s,
      id: byTitle.get(s.title.toLowerCase())?.id ?? nextId(),
    }))
  );
}

/**
 * A single stage row. Drag is bound to the grip alone — the row also hosts an
 * inline-editable title, and a whole-row drag target would swallow the click
 * that focuses it.
 *
 * Ref-forwarded for AnimatePresence's `popLayout` measurement, so removing a
 * stage lets the rows below slide up while the removed one is still exiting.
 */
const StageRow = forwardRef<
  HTMLLIElement,
  {
    stage: EditableStage;
    onRename: (title: string) => void;
    onRemove: () => void;
    canRemove: boolean;
  }
>(function StageRow({ stage, onRename, onRemove, canRemove }, ref) {
  const controls = useDragControls();
  const shouldReduce = useReducedMotion();

  return (
    <Reorder.Item
      ref={ref}
      value={stage}
      dragListener={false}
      dragControls={controls}
      layout
      initial={{ opacity: 0, height: 0, marginBottom: 0 }}
      animate={{ opacity: 1, height: "auto", marginBottom: 6 }}
      exit={{ opacity: 0, height: 0, marginBottom: 0, transition: { duration: 0.18 } }}
      transition={shouldReduce ? { duration: 0 } : SPRING}
      whileDrag={{
        scale: 1.02,
        boxShadow: "0 8px 24px -8px rgb(0 0 0 / 0.25)",
        zIndex: 20,
      }}
      className="list-none"
    >
      <div className="group flex items-center gap-2 rounded-lg border border-border/70 bg-background px-2 py-1.5">
        <button
          type="button"
          onPointerDown={(e) => controls.start(e)}
          aria-label={`Reorder ${stage.title}`}
          className="cursor-grab touch-none text-muted-foreground/40 transition-colors hover:text-muted-foreground active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" />
        </button>

        <motion.span
          layout
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: stage.colour }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={SPRING}
        />

        <input
          value={stage.title}
          onChange={(e) => onRename(e.target.value)}
          aria-label="Stage name"
          className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-medium text-foreground outline-none focus:ring-0"
        />

        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${stage.title}`}
            className="rounded p-1 text-muted-foreground/0 transition-colors hover:bg-destructive/10 hover:text-destructive group-hover:text-muted-foreground/60"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </Reorder.Item>
  );
});

/**
 * Step 3 — confirm the pipeline.
 *
 * Deliberately gated: the deal step can't run until the user explicitly locks
 * this in, because every deal created afterwards lands in one of these stages
 * and renaming them later is messier than getting them right now.
 */
export function PipelineStep({
  stages,
  onChange,
  onInstruction,
  onConfirm,
  isSubmitting,
  isParsing,
}: {
  stages: EditableStage[];
  onChange: (next: EditableStage[]) => void;
  /** Sends a plain-English edit to the parser. */
  onInstruction: (instruction: string) => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  isParsing: boolean;
}) {
  const [instruction, setInstruction] = useState("");

  const submitInstruction = () => {
    const text = instruction.trim();
    if (!text || isParsing) return;
    setInstruction("");
    onInstruction(text);
  };

  const hasBlankTitle = stages.some((s) => !s.title.trim());

  return (
    <StepCard>
      <div className="space-y-3">
        <StepCardLabel>Your pipeline</StepCardLabel>

        <Reorder.Group
          axis="y"
          values={stages}
          onReorder={(next) => onChange(renumber(next as EditableStage[]))}
          className="m-0 list-none p-0"
        >
          <AnimatePresence initial={false} mode="popLayout">
            {stages.map((stage) => (
              <StageRow
                key={stage.id}
                stage={stage}
                canRemove={stages.length > 2}
                onRename={(title) =>
                  onChange(stages.map((s) => (s.id === stage.id ? { ...s, title } : s)))
                }
                onRemove={() => onChange(renumber(stages.filter((s) => s.id !== stage.id)))}
              />
            ))}
          </AnimatePresence>
        </Reorder.Group>

        <button
          type="button"
          onClick={() =>
            onChange(
              renumber([
                ...stages,
                {
                  id: nextId(),
                  title: "New stage",
                  colour: "",
                  description: "",
                  order: stages.length + 1,
                },
              ])
            )
          }
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
          Add stage
        </button>

        {/* Natural-language editing. Drag covers reordering; this covers
            everything that's faster to say than to do. */}
        <div className="border-t border-border/60 pt-3">
          <div className="relative">
            <MessageSquareText
              className={cn(
                "pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 transition-colors",
                isParsing ? "text-primary" : "text-muted-foreground/50"
              )}
            />
            <Input
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitInstruction();
                }
              }}
              disabled={isParsing}
              placeholder='Or tell me: "add Screening between New and Reviewing"'
              className="h-9 pl-9 pr-9 text-sm"
            />
            <AnimatePresence>
              {isParsing && (
                <motion.div
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.6 }}
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <ExampleChips
            className="mt-2"
            examples={PIPELINE_EXAMPLES}
            onPick={setInstruction}
            disabled={isParsing}
          />
        </div>

        <div className="flex items-center justify-between gap-3 pt-1">
          <p className="text-xs text-muted-foreground">
            {stages.length} stages · drag to reorder
          </p>
          <Button
            onClick={onConfirm}
            disabled={isSubmitting || stages.length < 2 || hasBlankTitle}
            className="gap-2"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Check className="h-4 w-4" strokeWidth={3} />
                Lock it in
              </>
            )}
          </Button>
        </div>
      </div>
    </StepCard>
  );
}
